import store from '@/store'
import { useTransactionsStore } from '@modules/shared/stores/transactions'
import { getEntityId } from '@/helpers/utils'
import { fetchTradingPairs, fetchSparklines } from '@modules/trading/services/tradingApi'
import { B2bPortalRight } from '@/enums/B2bPortalRight'
import { FIAT_CURRENCIES } from '@/constants/currency'
import {
  SPARKLINES_CACHE_KEY,
  ACCOUNTS_CACHE_KEY,
  TRANSACTIONS_CACHE_KEY,
  STATS_TRANSACTIONS_CACHE_KEY,
  CARDS_CACHE_KEY,
  CARD_TYPES_CACHE_KEY,
  CURRENCIES_CACHE_KEY,
  COMPANY_LIST_CACHE_KEY,
  TARIFF_CACHE_KEY,
  COMPANY_DATA_CACHE_KEY,
  clearPreloadCacheStorage,
} from '@modules/app/composables/preloadCacheKeys'

// Vuex store state is untyped — use `any` for module access
const storeState = store.state as any

function userHasAccountView(): boolean {
  const rights = storeState.auth?.permissions?.rights
  return Array.isArray(rights) && rights.includes(B2bPortalRight.AccountView)
}

let preloading = false
let preloadResolve: (() => void) | null = null
let coreDataPromise: Promise<void> | null = null

/**
 * Returns a promise that resolves once Phase 1 (accounts + transactions)
 * has completed. The router guard awaits this before allowing navigation
 * to the dashboard so that accountsFetched is guaranteed to be true.
 */
export function waitForCoreData(): Promise<void> {
  if (storeState.accounts?.accountsFetched) return Promise.resolve()
  if (coreDataPromise) return coreDataPromise
  // If preload hasn't started yet, return a promise that will resolve when it does
  coreDataPromise = new Promise<void>((resolve) => { preloadResolve = resolve })
  return coreDataPromise
}

/**
 * Two-phase preloader:
 *   Phase 1 (priority): accounts + transactions — what Dashboard needs
 *   Phase 2 (deferred): cards, currencies, company list, tariff
 *
 * This prevents cards artwork sub-requests from saturating the browser's
 * 6-connection limit and blocking Dashboard-critical data.
 */
export function preloadCoreData() {
  if (preloading) return
  preloading = true

  // Restore ALL caches from sessionStorage immediately (sync, no network)
  restoreAccountsCache()
  restoreCompanyDataCache()
  restoreCardsCache()
  restoreSettingsCache()

  let txStore: ReturnType<typeof useTransactionsStore>
  try {
    txStore = useTransactionsStore()
    restoreTransactionsCache(txStore)
    restoreStatsTransactionsCache(txStore)
  } catch {
    // Pinia not ready yet — skip transactions, still do phased preload
    phase1WithoutTransactions()
    return
  }

  // Phase 1: Dashboard essentials + layout data
  const hasTxCache = txStore.transactions.length > 0
  const entityId = getEntityId()
  Promise.all([
    store.dispatch('accounts/fetchAccounts').catch(() => {}),
    hasTxCache
      ? txStore.fetchTransactions(false).catch(() => {})
      : txStore.fetchTransactions(true).catch(() => {}),
    entityId
      ? store.dispatch('getCompanyById', entityId).catch(() => {})
      : Promise.resolve(),
  ]).then(() => {
    saveAccountsCache()
    saveTransactionsCache(txStore)
    saveCompanyDataCache()

    // Phase 1 done — signal the router guard that accountsFetched is ready
    if (preloadResolve) { preloadResolve(); preloadResolve = null }
    coreDataPromise = null

    // Phase 2: Cards, settings, tariff, stats + trading/sparkline prefetch
    const statsRange = getDefaultStatsRange()
    // Start trading pairs prefetch (fires background fetches, populates sessionStorage)
    fetchTradingPairs().catch(() => {})
    // Prefetch sparklines for crypto accounts (background, non-blocking)
    prefetchSparklines()
    return Promise.all([
    //   store.dispatch('cards/fetchCards').catch(() => {}),
      store.dispatch('cards/getTypeOfCards').catch(() => {}),
    //   store.dispatch('cards/getDefaultAddress').catch(() => {}),
      store.dispatch('accounts/fetchAccountCurrencies').catch(() => {}),
      store.dispatch('getCompanyList').catch(() => {}),
      store.dispatch('getCompanyTariff').catch(() => {}),
      store.dispatch('refs/fetchCountryList').catch(() => {}),
      txStore.statsTransactions.length === 0
        ? txStore.fetchStatsTransactions(statsRange.from, statsRange.to).catch(() => {})
        : Promise.resolve(),
    ])
  }).finally(() => {
    saveCardsCache()
    saveSettingsCache()
    saveStatsTransactionsCache(txStore)
    preloading = false
  })
}

/**
 * Fallback when Pinia is not ready — no transactions preload
 */
function phase1WithoutTransactions() {
  const entityId = getEntityId()
  // Phase 1: accounts + company data
  Promise.all([
    store.dispatch('accounts/fetchAccounts').catch(() => {}),
    entityId
      ? store.dispatch('getCompanyById', entityId).catch(() => {})
      : Promise.resolve(),
  ]).then(() => {
    saveAccountsCache()
    saveCompanyDataCache()

    // Phase 1 done — signal the router guard
    if (preloadResolve) { preloadResolve(); preloadResolve = null }
    coreDataPromise = null

    // Phase 2: everything else
    return Promise.all([
      store.dispatch('cards/fetchCards').catch(() => {}),
      store.dispatch('cards/getTypeOfCards').catch(() => {}),
      store.dispatch('cards/getDefaultAddress').catch(() => {}),
      store.dispatch('accounts/fetchAccountCurrencies').catch(() => {}),
      store.dispatch('getCompanyList').catch(() => {}),
      store.dispatch('getCompanyTariff').catch(() => {}),
      store.dispatch('refs/fetchCountryList').catch(() => {}),
    ])
  }).finally(() => {
    saveCardsCache()
    saveSettingsCache()
    preloading = false
  })
}

/**
 * Reset preloader state (call on logout)
 */
export function clearPreloadCache() {
  preloading = false
  preloadResolve = null
  coreDataPromise = null
  clearPreloadCacheStorage()
}

// --- Sparkline prefetch ---

function prefetchSparklines() {
  const accounts = storeState.accounts?.accounts || []
  const cryptoSymbols = accounts
    .filter((a: any) => !FIAT_CURRENCIES.has(a.currency))
    .map((a: any) => a.currency.includes(':') ? a.currency.split(':').pop()! : a.currency)
  const unique = [...new Set<string>(cryptoSymbols)]
  if (unique.length === 0) return

  fetchSparklines(unique).then(data => {
    try {
      sessionStorage.setItem(SPARKLINES_CACHE_KEY, JSON.stringify(data))
    } catch { /* ignore */ }
  }).catch(() => {})
}

/**
 * Get cached sparklines (call from Crypto.vue / Accounts.vue)
 */
export function getCachedSparklines(): Record<string, number[]> | null {
  try {
    const raw = sessionStorage.getItem(SPARKLINES_CACHE_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return null
}

// --- sessionStorage helpers ---

function restoreAccountsCache() {
  if (!userHasAccountView()) return
  if (storeState.accounts?.accounts?.length) return
  try {
    const raw = sessionStorage.getItem(ACCOUNTS_CACHE_KEY)
    if (raw) {
      const accounts = JSON.parse(raw)
      if (Array.isArray(accounts) && accounts.length) {
        store.commit('accounts/SET_ACCOUNTS', accounts)
      }
    }
  } catch { /* ignore */ }
}

function saveAccountsCache() {
  try {
    if (!userHasAccountView()) return
    const accounts = storeState.accounts?.accounts
    if (accounts?.length) {
      sessionStorage.setItem(ACCOUNTS_CACHE_KEY, JSON.stringify(accounts))
    }
  } catch { /* ignore */ }
}

function restoreTransactionsCache(txStore: ReturnType<typeof useTransactionsStore>) {
  if (txStore.transactions.length) return
  try {
    const raw = sessionStorage.getItem(TRANSACTIONS_CACHE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      if (Array.isArray(data.transactions) && data.transactions.length) {
        txStore.transactions = data.transactions
        txStore.transactionsTotalCount = data.totalCount || 0
        txStore.transactionsOffset = data.offset || 0
      }
    }
  } catch { /* ignore */ }
}

function saveTransactionsCache(txStore: ReturnType<typeof useTransactionsStore>) {
  try {
    if (txStore.transactions.length) {
      sessionStorage.setItem(TRANSACTIONS_CACHE_KEY, JSON.stringify({
        transactions: txStore.transactions,
        totalCount: txStore.transactionsTotalCount,
        offset: txStore.transactionsOffset,
      }))
    }
  } catch { /* ignore */ }
}

function restoreCardsCache() {
  if (storeState.cards?.cards?.length) return
  try {
    const rawCards = sessionStorage.getItem(CARDS_CACHE_KEY)
    if (rawCards) {
      const cards = JSON.parse(rawCards)
      if (Array.isArray(cards) && cards.length) {
        store.commit('cards/SET_CARDS', cards)
      }
    }
    const rawTypes = sessionStorage.getItem(CARD_TYPES_CACHE_KEY)
    if (rawTypes) {
      const cardTypes = JSON.parse(rawTypes)
      if (Array.isArray(cardTypes) && cardTypes.length) {
        store.commit('cards/SET_CARD_TYPES', cardTypes)
      }
    }
  } catch { /* ignore */ }
}

function saveCardsCache() {
  try {
    const cards = storeState.cards?.cards
    if (cards?.length) {
      sessionStorage.setItem(CARDS_CACHE_KEY, JSON.stringify(cards))
    }
    const cardTypes = storeState.cards?.cardTypes
    if (cardTypes?.length) {
      sessionStorage.setItem(CARD_TYPES_CACHE_KEY, JSON.stringify(cardTypes))
    }
  } catch { /* ignore */ }
}

function restoreSettingsCache() {
  // Currencies (AccountView — same as GET /api/accounts/currencies)
  if (userHasAccountView() && !storeState.accounts?.currencies?.length) {
    try {
      const raw = sessionStorage.getItem(CURRENCIES_CACHE_KEY)
      if (raw) {
        const currencies = JSON.parse(raw)
        if (Array.isArray(currencies) && currencies.length) {
          store.commit('accounts/SET_ACCOUNT_CURRENCIES', currencies)
        }
      }
    } catch { /* ignore */ }
  }

  // Company list (auth module — not namespaced, state at store.state.auth)
  if (!storeState.auth?.company_list?.length) {
    try {
      const raw = sessionStorage.getItem(COMPANY_LIST_CACHE_KEY)
      if (raw) {
        const list = JSON.parse(raw)
        if (list) {
          store.commit('SET_COMPANY_LIST', list)
        }
      }
    } catch { /* ignore */ }
  }

  // Tariff (auth module — not namespaced, state at store.state.auth)
  if (!storeState.auth?.tariff) {
    try {
      const raw = sessionStorage.getItem(TARIFF_CACHE_KEY)
      if (raw) {
        const tariff = JSON.parse(raw)
        if (tariff) {
          store.commit('SET_TARIFF', tariff)
        }
      }
    } catch { /* ignore */ }
  }
}

function saveSettingsCache() {
  try {
    if (userHasAccountView()) {
      const currencies = storeState.accounts?.currencies
      if (currencies?.length) {
        sessionStorage.setItem(CURRENCIES_CACHE_KEY, JSON.stringify(currencies))
      }
    }
    const companyList = storeState.auth?.company_list
    if (companyList) {
      sessionStorage.setItem(COMPANY_LIST_CACHE_KEY, JSON.stringify(companyList))
    }
    const tariff = storeState.auth?.tariff
    if (tariff) {
      sessionStorage.setItem(TARIFF_CACHE_KEY, JSON.stringify(tariff))
    }
  } catch { /* ignore */ }
}

function getDefaultStatsRange(): { from: string; to: string } {
  const now = new Date()
  const to = now.toISOString().split('T')[0]
  const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
  return { from, to }
}

function restoreStatsTransactionsCache(txStore: ReturnType<typeof useTransactionsStore>) {
  if (txStore.statsTransactions.length) return
  try {
    const raw = sessionStorage.getItem(STATS_TRANSACTIONS_CACHE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      if (Array.isArray(data.transactions) && data.transactions.length) {
        const range = getDefaultStatsRange()
        // Only restore if cached range matches current default range
        if (data.from === range.from && data.to === range.to) {
          txStore.statsTransactions = data.transactions
        }
      }
    }
  } catch { /* ignore */ }
}

function saveStatsTransactionsCache(txStore: ReturnType<typeof useTransactionsStore>) {
  try {
    if (txStore.statsTransactions.length) {
      const range = getDefaultStatsRange()
      sessionStorage.setItem(STATS_TRANSACTIONS_CACHE_KEY, JSON.stringify({
        transactions: txStore.statsTransactions,
        from: range.from,
        to: range.to,
      }))
    }
  } catch { /* ignore */ }
}

function restoreCompanyDataCache() {
  if (storeState.auth?.current_company) return
  try {
    const raw = sessionStorage.getItem(COMPANY_DATA_CACHE_KEY)
    if (raw) {
      const data = JSON.parse(raw)
      if (data?.company) {
        store.commit('SET_CURRENT_COMPANY', data.company)
      }
      if (data?.user) {
        store.commit('SET_CURRENT_USER', data.user)
      }
    }
  } catch { /* ignore */ }
}

function saveCompanyDataCache() {
  try {
    const company = storeState.auth?.current_company
    const user = storeState.auth?.current_user
    if (company) {
      sessionStorage.setItem(COMPANY_DATA_CACHE_KEY, JSON.stringify({
        company,
        user,
      }))
    }
  } catch { /* ignore */ }
}
